import React, { useState } from 'react';
import { AppBar, Tabs, Tab } from '@mui/material';
import { toast } from 'react-toastify';
import FacultyDetails from './FacultyDetails';
import { useAuth } from "../../AuthContext";  
import configData from '../../configure';
import ClassesTab from './ClassesTab';
import ClassSchedule from '../Students/ClassSchedule';
import ClassScheduleTab from './ClassScheduleTab';
import '../Users/UserProfileModule.css';

function FacultyProfile({ selectedUser: initialUser, onUserUpdate }) {
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [currentTab, setCurrentTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const { axios  ,token} = useAuth();

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    // Create a deep copy of the selectedUser for editing
    setEditedUser({
      user: {
        ...selectedUser.rawData?.user,
        addressDetails: selectedUser.rawData?.user?.addressDetails
          ? [...selectedUser.rawData.user.addressDetails]
          : [{ addressType: 'CURRENT' }],
        additionalAttributes: {
          ...(selectedUser.rawData?.user?.additionalAttributes || {}),
        },
      },
      facultyInfo: {
        ...selectedUser.rawData?.facultyInfo,
        responsibilities: Array.isArray(selectedUser.rawData?.facultyInfo?.responsibilities)
          ? [...selectedUser.rawData.facultyInfo.responsibilities]
          : [],
      },
      studentInfo: null,
      parentInfo: null,
      staffInfo: null,
    });
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split('.');

    setEditedUser((prev) => {
      const newState = { ...prev };

      if (nameParts.length === 1) {
        newState[nameParts[0]] = value;
      } else if (nameParts.length === 2) {
        newState[nameParts[0]] = {
          ...newState[nameParts[0]],
          [nameParts[1]]: value,
        };
      } else if (nameParts.length === 3) {
        newState[nameParts[0]] = {
          ...newState[nameParts[0]],
          [nameParts[1]]: {
            ...newState[nameParts[0]][nameParts[1]],
            [nameParts[2]]: value,
          },
        };
      } else if (nameParts.length === 4 && nameParts[1] === 'addressDetails') {
        const index = parseInt(nameParts[2]);
        const field = nameParts[3];
        newState[nameParts[0]] = {
          ...newState[nameParts[0]],
          [nameParts[1]]: [
            ...newState[nameParts[0]][nameParts[1]].slice(0, index),
            {
              ...newState[nameParts[0]][nameParts[1]][index],
              [field]: value,
            },
            ...newState[nameParts[0]][nameParts[1]].slice(index + 1),
          ],
        };
      }

      // Special handling for responsibilities
      if (name === 'facultyInfo.responsibilities') {
        const responsibilities = value.split(',').map((item) => item.trim()).filter((item) => item !== '');
        newState.facultyInfo = {
          ...newState.facultyInfo,
          responsibilities,
        };
      }

      return newState;
    });
  };

  const validateForm = () => {
    const errors = [];
    if (!editedUser?.user?.firstName) errors.push('First Name is required');
    if (!editedUser?.user?.lastName) errors.push('Last Name is required');
    if (!editedUser?.user?.email || !/\S+@\S+\.\S+/.test(editedUser.user.email)) errors.push('Valid Email is required');
    if (!editedUser?.user?.phoneNumber || !/^\d{10}$/.test(editedUser.user.phoneNumber)) errors.push('Valid 10-digit Phone Number is required');
    if (editedUser?.user?.lastFourDigitsOfAadhaarNumber && !/^\d{4}$/.test(editedUser.user.lastFourDigitsOfAadhaarNumber)) errors.push('Last Four Digits of Aadhaar must be 4 digits');
    if (!editedUser?.user?.dateOfBirth) errors.push('Date of Birth is required');
    if (!editedUser?.user?.gender) errors.push('Gender is required');
    if (!editedUser?.user?.addressDetails?.[0]?.city) errors.push('City is required');
    if (!editedUser?.facultyInfo?.facultyType) errors.push('Faculty Type is required');
    if (editedUser?.facultyInfo?.facultyType !== 'SAMANVAYKARTA' && !editedUser?.facultyInfo?.supervisorId) {
      errors.push('Supervisor ID is required');
    }
    return errors;
  };

  const handleSaveClick = async () => {
    setLoading(true);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join(', '));
      setLoading(false);
      return;
    }

    try {
      const currentTime = new Date().toISOString();
      const dob = editedUser.user.dateOfBirth ? new Date(editedUser.user.dateOfBirth).toISOString() : '';
      const joiningDate = editedUser.facultyInfo.joiningDate ? new Date(editedUser.facultyInfo.joiningDate).toISOString() : '';

      const updateData = {
        user: {
          ...editedUser.user,
          userId: selectedUser.rawData?.user?.userId,
          orgId: selectedUser.rawData?.user?.orgId || 'default-org',
          userType: selectedUser.rawData?.user?.userType || 'FACULTY',
          roleId: selectedUser.rawData?.user?.roleId || 'FACULTY_ROLE',
          userStatus: selectedUser.rawData?.user?.userStatus || 'ACTIVE',
          createdBy: selectedUser.rawData?.user?.createdBy || 'system',
          createdAt: selectedUser.rawData?.user?.createdAt || currentTime,
          lastUpdatedBy: 'system',
          lastUpdatedAt: currentTime,
          email: editedUser.user.email,
          phoneNumber: editedUser.user.phoneNumber,
          lastFourDigitsOfAadhaarNumber: parseInt(editedUser.user.lastFourDigitsOfAadhaarNumber) || 0,
          firstName: editedUser.user.firstName,
          middleName: editedUser.user.middleName || 'unknown',
          lastName: editedUser.user.lastName,
          dateOfBirth: dob,
          gender: editedUser.user.gender.toUpperCase(),
          placeOfBirth: editedUser.user.placeOfBirth || 'unknown',
          bloodGroup: editedUser.user.bloodGroup || 'A_POSITIVE',
          nationality: editedUser.user.nationality || 'India',
          religion: editedUser.user.religion || 'unknown',
          category: editedUser.user.category || 'unknown',
          addressDetails: [
            {
              addressType: 'CURRENT',
              addressLine1: editedUser.user.addressDetails?.[0]?.addressLine1 || '',
              addressLine2: editedUser.user.addressDetails?.[0]?.addressLine2 || '',
              city: editedUser.user.addressDetails?.[0]?.city,
              state: editedUser.user.addressDetails?.[0]?.state || 'unknown',
              country: editedUser.user.addressDetails?.[0]?.country || 'India',
              pinCode: editedUser.user.addressDetails?.[0]?.pinCode || '000000',
              alternatePhoneNumber: editedUser.user.addressDetails?.[0]?.alternatePhoneNumber || '',
            },
          ],
        },
        facultyInfo: {
          ...editedUser.facultyInfo,
          userId: selectedUser.rawData?.user?.userId,
          createdBy: selectedUser.rawData?.facultyInfo?.createdBy || 'system',
          createdAt: selectedUser.rawData?.facultyInfo?.createdAt || currentTime,
          lastUpdatedBy: 'system',
          lastUpdatedAt: currentTime,
          facultyType: editedUser.facultyInfo.facultyType,
          employeeId: editedUser.facultyInfo.employeeId,
          designation: editedUser.facultyInfo.designation,
          department: editedUser.facultyInfo.department,
          joiningDate: joiningDate,
          responsibilities: editedUser.facultyInfo.responsibilities.length > 0 ? editedUser.facultyInfo.responsibilities : ['unknown'],
          facultyContractType: editedUser.facultyInfo.facultyContractType,
          supervisorId: editedUser.facultyInfo.supervisorId === 'none' ? null : (editedUser.facultyInfo.supervisorId || null),
        },
        studentInfo: null,
        parentInfo: null,
        staffInfo: null,
      };

      console.log('Sending update data:', JSON.stringify(updateData, null, 2));

      const response = await fetch(`${configData.UPDATE_USER}/${selectedUser.rawData?.user?.userId}`, {
        method: 'PUT',
        headers: {
          accept: 'application/hal+json',
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Update successful:', result);

      const updatedUser = {
        ...selectedUser,
        firstName: editedUser.user.firstName,
        middleName: editedUser.user.middleName,
        lastName: editedUser.user.lastName,
        email: editedUser.user.email,
        phoneNumber: editedUser.user.phoneNumber,
        gender: editedUser.user.gender,
        dateOfBirth: editedUser.user.dateOfBirth,
        placeOfBirth: editedUser.user.placeOfBirth,
        bloodGroup: editedUser.user.bloodGroup,
        nationality: editedUser.user.nationality,
        religion: editedUser.user.religion,
        category: editedUser.user.category,
        lastFourDigitsOfAadhaarNumber: editedUser.user.lastFourDigitsOfAadhaarNumber,
        rawData: {
          user: result.user,
          facultyInfo: result.facultyInfo,
        },
      };

      setSelectedUser(updatedUser);
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      toast.success('Faculty information updated successfully!');
      setIsEditing(false);
      setEditedUser(null);
    } catch (err) {
      console.error('Error updating faculty:', err);
      toast.error(`Failed to update: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-module">
      <AppBar position="static" className="profile-tab-bar" style={{ boxShadow: 'none' }}>
        <Tabs id="customTabs" value={currentTab} onChange={handleChangeTab}>
          <Tab label="Faculty Profile" />
          <Tab label="Classes" />
          <Tab label="Schedule" />
          <Tab label="Documents" />
        </Tabs>
      </AppBar>



      {currentTab === 0 && (
        <FacultyDetails
          selectedUser={selectedUser}
          handleChange={handleChange}
          editedUser={editedUser}
          isEditing={isEditing}
          handleEditClick={handleEditClick}
          handleCancelClick={handleCancelClick}
          handleSaveClick={handleSaveClick}
          loading={loading}
        />
      )}
     {currentTab === 1 && (
  <ClassesTab selectedUser={selectedUser } />
)}
      {currentTab === 2 && (
         <ClassScheduleTab selectedUser={selectedUser } />

      )}
      {currentTab === 3 && (
        <div>
          <h3>Documents</h3>
          <p>Faculty  records...</p>
        </div>
      )}
      {currentTab === 4 && (
        <div>
          <h3>Performance</h3>
          <p>Faculty performance metrics...</p>
        </div>
      )}
    </div>
  );
}

export default FacultyProfile;